import { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Slider,
  TextField,
  MenuItem,
  Select,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { X as CloseIcon, ChevronDown } from "lucide-react";
import CustomButton from "../Button/Custombutton";
import { useTheme } from "../../../../core/providers/ThemeProvider";
import { useGetDistancePreferencesQuery } from "../../../../api/userApi";
import { useUserStore } from "../../../../app/store/useUserStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { useLocalization } from "../../../contexts/LocalizationContext";
import type { WeightUnit } from "../../../utils/localizationService";
import { SEARCH_DEFAULTS } from "../../../styles/theme/theme";

export interface FilterValues {
  radius: number; // in meters
  minWeight: number; // in kg or lb depending on unit
  maxWeight: number; // in kg or lb depending on unit
  weightUnit: "lb";
  sortBy: "latest" | "price_high_to_low" | "price_low_to_high" | "rating_desc" | "rating_asc";
}

interface FilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterValues) => void;
  initialFilters?: Partial<FilterValues>;
}

const FilterDialog: React.FC<FilterDialogProps> = ({
  open,
  onClose,
  onApply,
  initialFilters,
}) => {
  const { theme } = useTheme();
  const primaryColor = theme?.colors?.primary || "#7A0056";
  const zustandUser = useUserStore.getState().user;
  const { t } = useTranslation();
  const { convertWeight, getWeightUnit } = useLocalization();

  // Default values from centralized constants
  const DEFAULT_RADIUS = SEARCH_DEFAULTS.RADIUS;
  const DEFAULT_MIN_WEIGHT = SEARCH_DEFAULTS.MIN_WEIGHT;
  const DEFAULT_MAX_WEIGHT = SEARCH_DEFAULTS.MAX_WEIGHT;
  const DEFAULT_SORT_BY = SEARCH_DEFAULTS.SORT_BY;
  const WEIGHT_UNIT = SEARCH_DEFAULTS.WEIGHT_UNIT;

  // Local state for filter values
  const [radius, setRadius] = useState<number>(
    initialFilters?.radius ?? DEFAULT_RADIUS
  );
  // weightRange/minWeightInput/maxWeightInput are always in lbs — the value sent to the API via
  // onApply. weightUnit only controls what's displayed/typed; conversion happens at the edges.
  const [weightRange, setWeightRange] = useState<number[]>([
    initialFilters?.minWeight ?? DEFAULT_MIN_WEIGHT,
    initialFilters?.maxWeight ?? DEFAULT_MAX_WEIGHT
  ]);
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(getWeightUnit());
  const [minWeightInput, setMinWeightInput] = useState<string>(
    convertWeight(initialFilters?.minWeight ?? DEFAULT_MIN_WEIGHT, 'lbs', weightUnit).toFixed(0)
  );
  const [maxWeightInput, setMaxWeightInput] = useState<string>(
    convertWeight(initialFilters?.maxWeight ?? DEFAULT_MAX_WEIGHT, 'lbs', weightUnit).toFixed(0)
  );
  const [sortBy, setSortBy] = useState<FilterValues["sortBy"]>(
    initialFilters?.sortBy || DEFAULT_SORT_BY
  );

  // Sync local state with initialFilters whenever the dialog opens
  useEffect(() => {
    if (open) {
      setRadius(initialFilters?.radius ?? DEFAULT_RADIUS);
      const min = initialFilters?.minWeight ?? DEFAULT_MIN_WEIGHT;
      const max = initialFilters?.maxWeight ?? DEFAULT_MAX_WEIGHT;
      setWeightRange([min, max]);
      setMinWeightInput(convertWeight(min, 'lbs', weightUnit).toFixed(0));
      setMaxWeightInput(convertWeight(max, 'lbs', weightUnit).toFixed(0));
      setSortBy(initialFilters?.sortBy || DEFAULT_SORT_BY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFilters]);

  // Max values for slider, in lbs (capped at MAX_WEIGHT) and in the active display unit
  const maxSliderValueLbs = SEARCH_DEFAULTS.MAX_WEIGHT;
  const maxSliderValue = convertWeight(maxSliderValueLbs, 'lbs', weightUnit);

  // Handle unit dropdown change — convert the currently selected range in place
  const handleWeightUnitChange = (newUnit: WeightUnit) => {
    setMinWeightInput(convertWeight(weightRange[0], 'lbs', newUnit).toFixed(0));
    setMaxWeightInput(convertWeight(weightRange[1], 'lbs', newUnit).toFixed(0));
    setWeightUnit(newUnit);
  };

  // Handle weight slider change (values arrive in the active display unit; convert to lbs for state)
  const handleWeightSliderChange = (_event: Event, newValue: number | number[]) => {
    const values = newValue as number[];
    const lbsValues = [
      convertWeight(values[0], weightUnit, 'lbs'),
      convertWeight(values[1], weightUnit, 'lbs'),
    ];
    setWeightRange(lbsValues);
    setMinWeightInput(values[0].toString());
    setMaxWeightInput(values[1].toString());
  };

  // Handle min weight input change (typed value is in the active display unit)
  const handleMinWeightInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      setMinWeightInput('');
      setWeightRange([0, weightRange[1]]);
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    const numValue = Number(value);

    if (numValue < 0 || numValue > maxSliderValue) {
      return;
    }

    setMinWeightInput(value);
    setWeightRange([convertWeight(numValue, weightUnit, 'lbs'), weightRange[1]]);
  };

  // Handle max weight input change (typed value is in the active display unit)
  const handleMaxWeightInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      setMaxWeightInput('');
      setWeightRange([weightRange[0], maxSliderValueLbs]);
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    const numValue = Number(value);

    if (numValue < 0 || numValue > maxSliderValue) {
      return;
    }

    setMaxWeightInput(value);
    setWeightRange([weightRange[0], convertWeight(numValue, weightUnit, 'lbs')]);
  };

  // Handle apply button — weightRange is always lbs, so the payload sent to the API is unaffected
  // by the display unit the user picked.
  const handleApply = () => {
    const filters: FilterValues = {
      radius,
      minWeight: weightRange[0],
      maxWeight: weightRange[1],
      sortBy,
      weightUnit: WEIGHT_UNIT,
    };
    onApply(filters);
    onClose();
  };

  // Handle reset button
  const handleReset = () => {
    setRadius(DEFAULT_RADIUS);
    setWeightRange([DEFAULT_MIN_WEIGHT, DEFAULT_MAX_WEIGHT]);
    setMinWeightInput(convertWeight(DEFAULT_MIN_WEIGHT, 'lbs', weightUnit).toFixed(0));
    setMaxWeightInput(convertWeight(DEFAULT_MAX_WEIGHT, 'lbs', weightUnit).toFixed(0));
    setSortBy(DEFAULT_SORT_BY);

    const defaultFilters: FilterValues = {
      radius: DEFAULT_RADIUS,
      minWeight: DEFAULT_MIN_WEIGHT,
      maxWeight: DEFAULT_MAX_WEIGHT,
      sortBy: DEFAULT_SORT_BY,
      weightUnit: WEIGHT_UNIT,
    };
    onApply(defaultFilters);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            boxShadow:
              "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)",
            width: { xs: "90%", sm: "430px" },
            maxWidth: "430px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            p: 0,
            m: 2,
            overflow: "hidden",
          },
        },
      }}
    >
      {/* Modal Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "16px 20px",
          borderBottom: "1px solid #E9EAEB",
          flexShrink: 0,
        }}
      >
        {/* Title */}
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: "18px",
            lineHeight: "27px",
            color: "#484A54",
          }}
        >
          {t('filterDialog.title')}
        </Typography>

        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            width: "28px",
            height: "28px",
            padding: 0,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon size={18} color="#5D606D" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: "16px 20px",
          gap: "12px",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D9DADF",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#D9DADF transparent",
        }}
      >
        {/* Radius Slider */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "21px",
              color: "#373940",
            }}
          >
            {t('filterDialog.radius')}
          </Typography>
          <Box sx={{ px: 0.5, pt: 1, pb: 2 }}>
            <Slider
              value={radius}
              min={0}
              max={500}
              onChange={(_e, val) => setRadius(val as number)}
              valueLabelDisplay="on"
              valueLabelFormat={(value) => `${value}`}
              sx={{
                color: primaryColor,
                height: 6,
                "& .MuiSlider-track": {
                  border: "none",
                  background: primaryColor,
                  borderRadius: "9999px",
                },
                "& .MuiSlider-rail": {
                  background: "#E9EAEB",
                  opacity: 1,
                  borderRadius: "9999px",
                },
                "& .MuiSlider-thumb": {
                  height: 20,
                  width: 20,
                  backgroundColor: "#FFFFFF",
                  border: `2px solid ${primaryColor}`,
                  boxShadow:
                    "0px 2px 4px -1px rgba(10, 13, 18, 0.1), 0px 1px 2px -1px rgba(10, 13, 18, 0.06)",
                  "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
                    boxShadow:
                      "0px 2px 4px -1px rgba(10, 13, 18, 0.1), 0px 1px 2px -1px rgba(10, 13, 18, 0.06)",
                  },
                  "&:before": {
                    display: "none",
                  },
                },
                "& .MuiSlider-valueLabel": {
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "16px",
                  color: "#484A54",
                  backgroundColor: "transparent",
                  top: 32,
                },
              }}
            />
          </Box>
        </Box>

        {/* Weight Range Slider */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "21px",
                color: "#373940",
              }}
            >
              {t('filterDialog.weight')} ({weightUnit})
            </Typography>

            {/* Weight Unit Toggle */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "13px",
                  lineHeight: "18px",
                  color: weightUnit === "lbs" ? "#373940" : "#999CA8",
                }}
              >
                {t('filterDialog.lbs')}
              </Typography>
              <Box
                role="switch"
                aria-checked={weightUnit === "kg"}
                aria-label={t('filterDialog.weightUnitToggleLabel')}
                tabIndex={0}
                onClick={() => handleWeightUnitChange(weightUnit === "lbs" ? "kg" : "lbs")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleWeightUnitChange(weightUnit === "lbs" ? "kg" : "lbs");
                  }
                }}
                sx={{
                  position: "relative",
                  width: "36px",
                  height: "20px",
                  borderRadius: "9999px",
                  backgroundColor: primaryColor,
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "background-color 0.2s",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "2px",
                    left: weightUnit === "kg" ? "18px" : "2px",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0px 1px 2px rgba(10, 13, 18, 0.15)",
                    transition: "left 0.2s",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "13px",
                  lineHeight: "18px",
                  color: weightUnit === "kg" ? "#373940" : "#999CA8",
                }}
              >
                {t('filterDialog.kg')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: 0.5, pt: 1, pb: 2 }}>
            <Slider
              value={[
                Number(convertWeight(weightRange[0], 'lbs', weightUnit).toFixed(2)),
                Number(convertWeight(weightRange[1], 'lbs', weightUnit).toFixed(2)),
              ]}
              min={0}
              max={Number(maxSliderValue.toFixed(2))}
              onChange={handleWeightSliderChange}
              valueLabelDisplay="on"
              valueLabelFormat={(value) => value.toFixed(2)}
              sx={{
                color: primaryColor,
                height: 6,
                "& .MuiSlider-track": {
                  border: "none",
                  background: primaryColor,
                  borderRadius: "9999px",
                },
                "& .MuiSlider-rail": {
                  background: "#E9EAEB",
                  opacity: 1,
                  borderRadius: "9999px",
                },
                "& .MuiSlider-thumb": {
                  height: 20,
                  width: 20,
                  backgroundColor: "#FFFFFF",
                  border: `2px solid ${primaryColor}`,
                  boxShadow:
                    "0px 2px 4px -1px rgba(10, 13, 18, 0.1), 0px 1px 2px -1px rgba(10, 13, 18, 0.06)",
                  "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
                    boxShadow:
                      "0px 2px 4px -1px rgba(10, 13, 18, 0.1), 0px 1px 2px -1px rgba(10, 13, 18, 0.06)",
                  },
                  "&:before": {
                    display: "none",
                  },
                },
                "& .MuiSlider-valueLabel": {
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "16px",
                  color: "#484A54",
                  backgroundColor: "transparent",
                  top: 32,
                },
              }}
            />
          </Box>
        </Box>

        {/* Weight Input Fields (Min and Max) */}
        <Box sx={{ display: "flex", gap: "12px" }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "18px",
                color: "#373940",
                mb: "6px",
              }}
            >
              {t('filterDialog.minWeight')}
            </Typography>
            <TextField
              placeholder="0"
              value={minWeightInput}
              onChange={handleMinWeightInputChange}
              type="text"
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      color: "#484A54",
                      fontWeight: 500,
                    }}
                  >
                    {weightUnit}
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                  borderRadius: "8px",
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#484A54",
                  "& fieldset": {
                    borderColor: "#ACAFBB",
                  },
                  "&:hover fieldset": {
                    borderColor: "#484A54",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "0 14px",
                  height: "100%",
                  "&::placeholder": {
                    color: "#999CA8",
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "18px",
                color: "#373940",
                mb: "6px",
              }}
            >
              {t('filterDialog.maxWeight')}
            </Typography>
            <TextField
              placeholder="60"
              value={maxWeightInput}
              onChange={handleMaxWeightInputChange}
              type="text"
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      color: "#484A54",
                      fontWeight: 500,
                    }}
                  >
                    {weightUnit}
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                  borderRadius: "8px",
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#484A54",
                  "& fieldset": {
                    borderColor: "#ACAFBB",
                  },
                  "&:hover fieldset": {
                    borderColor: "#484A54",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "0 14px",
                  height: "100%",
                  "&::placeholder": {
                    color: "#999CA8",
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Sort by Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            mt: "4px",
          }}
        >
          {/* Sort by Header */}
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "21px",
              color: "#373940",
            }}
          >
            {t('filterDialog.sortBy')}
          </Typography>

          {/* Price Sort Dropdown */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "18px",
                color: "#373940",
              }}
            >
              {t('filterDialog.price')}
            </Typography>
            <Select
              value={sortBy === "price_low_to_high" || sortBy === "price_high_to_low" ? sortBy : ""}
              onChange={(e: SelectChangeEvent) => setSortBy(e.target.value as FilterValues["sortBy"])}
              displayEmpty
              IconComponent={ChevronDown}
              sx={{
                height: "44px",
                borderRadius: "8px",
                fontFamily: "Poppins",
                fontSize: "14px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ACAFBB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#484A54",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: primaryColor,
                },
                "& .MuiSelect-select": {
                  padding: "10px 14px",
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#484A54",
                },
                "& .MuiSelect-icon": {
                  color: "#5D606D",
                  width: "20px",
                  height: "20px",
                },
              }}
            >
              <MenuItem value="" disabled sx={{ fontFamily: "Poppins", fontSize: "14px", color: "#999CA8" }}>
                {t('filterDialog.selectOption')}
              </MenuItem>
              <MenuItem value="price_low_to_high" sx={{ fontFamily: "Poppins", fontSize: "14px" }}>
                {t('filterDialog.sortOptions.priceLowToHigh')}
              </MenuItem>
              <MenuItem value="price_high_to_low" sx={{ fontFamily: "Poppins", fontSize: "14px" }}>
                {t('filterDialog.sortOptions.priceHighToLow')}
              </MenuItem>
            </Select>
          </Box>

          {/* Rating Sort Dropdown */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mt: "4px" }}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "18px",
                color: "#373940",
              }}
            >
              {t('filterDialog.rating')}
            </Typography>
            <Select
              value={sortBy === "rating_desc" || sortBy === "rating_asc" ? sortBy : ""}
              onChange={(e: SelectChangeEvent) => setSortBy(e.target.value as FilterValues["sortBy"])}
              displayEmpty
              IconComponent={ChevronDown}
              sx={{
                height: "44px",
                borderRadius: "8px",
                fontFamily: "Poppins",
                fontSize: "14px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ACAFBB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#484A54",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: primaryColor,
                },
                "& .MuiSelect-select": {
                  padding: "10px 14px",
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#484A54",
                },
                "& .MuiSelect-icon": {
                  color: "#5D606D",
                  width: "20px",
                  height: "20px",
                },
              }}
            >
              <MenuItem value="" disabled sx={{ fontFamily: "Poppins", fontSize: "14px", color: "#999CA8" }}>
                {t('filterDialog.selectOption')}
              </MenuItem>
              <MenuItem value="rating_desc" sx={{ fontFamily: "Poppins", fontSize: "14px" }}>
                {t('filterDialog.sortOptions.ratingHighToLow')}
              </MenuItem>
              <MenuItem value="rating_asc" sx={{ fontFamily: "Poppins", fontSize: "14px" }}>
                {t('filterDialog.sortOptions.ratingLowToHigh')}
              </MenuItem>
            </Select>
          </Box>
        </Box>
      </Box>

      {/* Modal Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: "16px 20px",
          gap: "12px",
          borderTop: "1px solid #E9EAEB",
          flexShrink: 0,
        }}
      >
        {/* Reset Filters Button */}
        <CustomButton
          buttonText={t('filterDialog.resetFilters')}
          showButtonText
          onClick={handleReset}
          sx={{
            padding: "10px 24px",
            minHeight: "40px",
            background: "#FFFFFF",
            border: "1px solid #D9DADF",
            borderRadius: "8px",
            fontFamily: "Poppins",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "21px",
            color: "#484A54",
            textTransform: "none",
            "&:hover": {
              background: "#F5F5F5",
            },
          }}
        />

        {/* Apply Filters Button */}
        <CustomButton
          buttonText={t('filterDialog.applyFilters')}
          showButtonText
          onClick={handleApply}
          sx={{
            padding: "10px 24px",
            minHeight: "40px",
            background: primaryColor,
            borderRadius: "8px",
            fontFamily: "Poppins",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "21px",
            color: "#FFFFFF",
            textTransform: "none",
            "&:hover": {
              background: `${primaryColor}DD`,
            },
          }}
        />
      </Box>
    </Dialog>
  );
};

export default FilterDialog;
